// Per-kind UI schema for the generic ResourceCRUDPage.
// Supported field types:
//   text | number | textarea | checkbox | select | csv | json | time
//   ref       — single-select from another resource kind {refKind, refLabelKey?}
//   multiref  — multi-select from another resource kind
//   file      — file upload {uploadKind, accept}

export const RESOURCE_SCHEMAS = {
  // ----- Voice / Telephony -----
  sip_trunks: {
    title: "SIP Trunks",
    subtitle: "Outbound + inbound SIP trunks. Connects to your PSTN provider via Asterisk.",
    columns: [
      { key: "data.provider", label: "Provider" },
      { key: "data.host",     label: "Host" },
      { key: "data.transport",label: "Transport" },
      { key: "data.max_channels", label: "Channels" },
      { key: "data.cps_limit", label: "CPS" },
    ],
    dataFields: [
      { key: "provider",    label: "Provider",       type: "select", options: ["plivo_zentrunk", "twilio_sip", "plivo_sip", "byo_sip", "tata_tele", "exotel", "knowlarity"], help: "Pick 'plivo_zentrunk' for the Plivo Zentrunk registration-based service (Indian POP recommended)." },
      { key: "host",        label: "SIP host",       type: "text", required: true, placeholder: "phone.plivo.com  (or sip.plivo.com)", help: "For Plivo Zentrunk India use phone.plivo.com (Mumbai POP); global default is sip.plivo.com." },
      { key: "port",        label: "Port",           type: "number", placeholder: "5060" },
      { key: "transport",   label: "Transport",      type: "select", options: ["UDP", "TCP", "TLS"] },
      { key: "register",    label: "Use SIP REGISTER (registration-based auth)", type: "checkbox", help: "Enable for Plivo Zentrunk / Twilio Elastic SIP. Disable for IP-auth trunks." },
      { key: "username",    label: "Username",       type: "text", required: true, placeholder: "Your Plivo Zentrunk SIP username" },
      { key: "password",    label: "Password",       type: "text", placeholder: "SIP auth password (stored in admin DB)", help: "Required when REGISTER is enabled. Stored alongside other trunk config; rotate via this field." },
      { key: "from_user",   label: "From-user (technical SIP user)", type: "text", placeholder: "Defaults to username", help: "Used in SIP From: header when initiating outbound calls." },
      { key: "from_number", label: "Caller-ID (default outbound DID)", type: "text", placeholder: "+918012345678", help: "E.164 number presented to the callee. Can be overridden per campaign / caller-ID pool." },
      { key: "max_channels",label: "Max channels",   type: "number", placeholder: "30", help: "Asterisk hard-caps concurrent calls at this number; 30 = Plivo Zentrunk default." },
      { key: "cps_limit",   label: "CPS limit (calls/sec)", type: "number", placeholder: "3", help: "Max outbound calls per second the dialer fires on this trunk. Carriers (Plivo, Twilio) drop or 503 calls above their CPS cap. Default 3. Leave blank to use the default." },
      { key: "dial_prefix", label: "Outbound dial prefix",    type: "text", placeholder: "(blank for none)", help: "Prepended to every dialed number, e.g. '9' for outside-line systems." },
      { key: "country_code", label: "Default country code", type: "text", placeholder: "91", help: "Used for E.164 normalization of 10-digit numbers (India = 91)." },
      { key: "codecs",      label: "Codecs",         type: "csv", placeholder: "ulaw,alaw" },
      { key: "inbound_match", label: "Inbound source IPs (extra)", type: "textarea", placeholder: "e.g. 52.x.x.x, 10.0.0.0/24", help: "Extra carrier source IPs/CIDRs allowed to send INBOUND calls (comma/space/newline separated). Plivo Zentrunk's published ranges are added automatically — only needed for BYO/other carriers or non-standard Plivo edges." },
    ],
  },

  // dids handled by a CUSTOM page (DIDsPage) — see admin/pages/DIDs.jsx
  // (Schema not used; left here for reference if reverted to generic.)

  extensions: {
    title: "Extensions",
    subtitle: "Internal dialplan extensions (e.g., 1001 → agent X, *97 → voicemail).",
    columns: [{ key: "data.extension", label: "Extension" }],
    dataFields: [
      { key: "extension", label: "Extension digits", type: "text", required: true, placeholder: "1001" },
      { key: "target_kind", label: "Target kind", type: "select", options: ["agent","queue","voicemail","ivr"] },
      { key: "target_label", label: "Target", type: "text" },
    ],
  },

  recording_rules: {
    title: "Recording Rules",
    subtitle: "Reusable recording profiles. Pick a rule on a campaign (outbound) or an inbound queue to control how those calls are recorded.",
    columns: [
      { key: "data.mode",   label: "Mode" },
      { key: "data.format", label: "Format" },
      { key: "data.consent_announce", label: "Consent" },
    ],
    dataFields: [
      { key: "mode", label: "Recording mode", type: "select",
        options: [
          { value: "always", label: "Always record" },
          { value: "off",    label: "Do not record (disable)" },
        ],
        help: "Always = every call using this rule is recorded. Off = a profile that explicitly disables recording (handy to override a default on a specific campaign/queue)." },
      { key: "format", label: "Audio format", type: "select",
        options: [
          { value: "wav", label: "WAV (default)" },
          { value: "mp3", label: "MP3 (needs format_mp3 in Asterisk)" },
        ] },
      { key: "stereo", label: "Stereo (split agent / customer channels)", type: "checkbox",
        help: "Stored on the recording. True dual-channel split applies on a live PBX." },
      { key: "max_duration_min", label: "Max recording length (minutes)", type: "number", placeholder: "0 = unlimited",
        help: "Stored with the recording for retention/compliance. 0 or blank = no limit." },
      { key: "consent_announce", label: "Play consent / disclosure prompt", type: "checkbox",
        help: "Outbound: played to the customer right after they answer, before the agent joins. Inbound: played to the caller before they enter the queue." },
      { key: "consent_template_id", label: "Consent template", type: "ref", refKind: "consent_templates",
        showIf: (f) => !!f.consent_announce,
        help: "The disclosure to play. Manage these under Compliance → Consent Templates." },
    ],
  },

  caller_id_pool: {
    title: "Caller-ID Pool",
    subtitle: "A named pool of outbound caller-IDs. Link a pool to a campaign to rotate numbers across calls and lift answer rates.",
    columns: [{ key: "data.rotation", label: "Rotation" }, { key: "data.numbers", label: "Numbers" }],
    dataFields: [
      { key: "rotation", label: "Rotation strategy", type: "select",
        options: [
          { value: "round_robin", label: "Even rotation (round-robin)" },
          { value: "weighted",    label: "Weighted (favour higher weight)" },
          { value: "local",       label: "Prefer local (match dialed number)" },
        ],
        help: "How the next caller-ID is chosen for each call. Round-robin cycles evenly across all numbers; weighted favours numbers with a higher weight; prefer-local picks the number whose match-prefix matches the dialed number's leading digits (falls back to round-robin when none match)." },
      { key: "numbers", label: "Caller-ID numbers", type: "rows", requiredKey: "number",
        columns: [
          { key: "number", label: "Caller-ID (DID)", placeholder: "+91 80 4744 7000", type: "text" },
          { key: "region", label: "Region label", placeholder: "Bengaluru", type: "text" },
          { key: "weight", label: "Weight", placeholder: "1", type: "number" },
          { key: "match_prefix", label: "Match prefix", placeholder: "9180", type: "text" },
        ],
        help: "Add one row per caller-ID. Each number MUST be a real DID your SIP trunk is allowed to present, or the carrier will reject the call. 'Weight' is used by weighted rotation; 'Match prefix' (leading digits, e.g. 9180) is used by prefer-local." },
    ],
  },

  music_on_hold: {
    title: "Music on Hold",
    subtitle: "Upload MP3/WAV files. Selectable in Queues, IVRs, and Campaigns.",
    columns: [{ key: "data.audio_url", label: "Audio" }, { key: "data.duration_seconds", label: "Length" }],
    dataFields: [
      { key: "audio_url", label: "Audio file", type: "file", uploadKind: "music_on_hold", accept: "audio/*" },
      { key: "duration_seconds", label: "Duration (seconds)", type: "number" },
      { key: "license", label: "License / source", type: "text" },
    ],
  },

  audio_prompts: {
    title: "Audio Prompts",
    subtitle: "Pre-recorded prompts for IVRs, voicemail, broadcasts.",
    columns: [{ key: "data.audio_url", label: "Audio" }, { key: "data.language", label: "Lang" }],
    dataFields: [
      { key: "audio_url", label: "Audio file", type: "file", uploadKind: "audio_prompts", accept: "audio/*" },
      { key: "language", label: "Language", type: "select", options: ["en-IN","en-US","hi-IN","es","fr"] },
      { key: "transcript", label: "Transcript (for STT fallback / accessibility)", type: "textarea" },
    ],
  },

  // ----- Routing / ACD -----
  queues: {
    title: "Inbound Queues",
    subtitle: "ACD queues — strategy, SLA, MoH, virtual hold, callback-in-queue.",
    columns: [
      { key: "data.strategy", label: "Strategy" },
      { key: "data.sla_target", label: "SLA %" },
      { key: "data.max_wait", label: "Max wait" },
    ],
    dataFields: [
      { key: "strategy",          label: "Distribution strategy", type: "select", options: ["round_robin","least_busy","longest_idle","skill_priority","most_qualified","fewest_calls"] },
      { key: "skills_required",   label: "Skills required",       type: "multiref", refKind: "skills", returnKey: "name" },
      { key: "music_on_hold_id",  label: "Music on hold",         type: "ref", refKind: "music_on_hold" },
      { key: "record_calls",      label: "Record inbound calls",  type: "checkbox", help: "Record calls answered through this queue. The selected recording rule controls format, stereo and the consent prompt." },
      { key: "recording_rule_id", label: "Recording rule",        type: "ref", refKind: "recording_rules",
        showIf: (f) => !!f.record_calls,
        help: "Which recording profile to apply to this queue's inbound calls. Manage under Compliance → Recording Rules. If left empty, calls record as WAV with no consent prompt." },
      { key: "working_hours_id",  label: "Working hours profile", type: "ref", refKind: "working_hours_profiles", help: "Optional. When set, calls outside these hours route to the after-hours destination below." },
      { key: "after_hours_target_kind", label: "After-hours route to", type: "select",
        options: [
          { value: "voicemail", label: "Voicemail box" },
          { value: "ivr", label: "IVR flow" },
          { value: "queue", label: "Another queue" },
          { value: "external", label: "External number" },
          { value: "play_audio_hangup", label: "Play after-hours prompt and hang up" },
          { value: "hangup", label: "Hang up" },
        ] },
      { key: "after_hours_target_id", label: "After-hours target", type: "ref",
        showIf: (f) => ["queue", "ivr", "voicemail"].includes(f.after_hours_target_kind),
        refKindFrom: (f) => ({ queue: "queues", ivr: "ivr_flows", voicemail: "voicemail_boxes" }[f.after_hours_target_kind]) },
      { key: "after_hours_external", label: "After-hours external number", type: "text", placeholder: "+44 20 7946 0958",
        showIf: (f) => f.after_hours_target_kind === "external" },
      { key: "after_hours_audio_id", label: "After-hours audio prompt", type: "ref", refKind: "audio_prompts",
        showIf: (f) => f.after_hours_target_kind === "play_audio_hangup" },
      { key: "linked_campaign_id", label: "Linked campaign (for blended)", type: "ref", refKind: "campaigns" },
      { key: "allowed_dispositions", label: "Allowed dispositions", type: "multiref", refKind: "dispositions", _catalog: "dispositions" },
      { key: "allowed_pause_codes", label: "Allowed pause codes", type: "multiref", refKind: "pause-codes", _catalog: "pause-codes" },
      { key: "max_wait",          label: "Max wait (seconds)",    type: "number", placeholder: "240" },
      { key: "wrap_up_seconds",   label: "Auto wrap-up (seconds, 0=off)", type: "number", placeholder: "30" },
      { key: "auto_wrapup_code_id", label: "Auto wrap-up code (if agent doesn't pick)", type: "ref", refKind: "wrapup-codes", _catalog: "wrapup-codes" },
      { key: "sla_target",        label: "SLA target %",          type: "number", placeholder: "80" },
      { key: "sla_seconds",       label: "SLA threshold (seconds)", type: "number", placeholder: "30" },
      { key: "announce_position", label: "Announce position",     type: "checkbox" },
      { key: "callback_in_queue", label: "Callback-in-queue",     type: "checkbox" },
      { key: "virtual_hold",      label: "Virtual hold",          type: "checkbox" },
      { key: "priority",          label: "Priority (1-10)",       type: "number", placeholder: "5" },
    ],
  },

  // ivr_flows handled by a CUSTOM page (IVRBuilder)
  // scorecards handled by a CUSTOM page (ScorecardBuilder)
  // campaigns handled by a CUSTOM page (CampaignEditor)
  // amd_settings is a SINGLETON (AMDSettings page)
  // lead_lists handled by a CUSTOM page (LeadLists with CSV import)
  // dnc_lists handled by a CUSTOM page (DNCLists with CSV import)

  skills: {
    title: "Skills",
    subtitle: "Skill matrix definitions — assigned to agents and required by queues / campaigns.",
    columns: [{ key: "data.weight_default", label: "Default weight" }, { key: "data.language", label: "Language" }],
    dataFields: [
      { key: "weight_default", label: "Default weight", type: "number", placeholder: "5" },
      { key: "language", label: "Language", type: "text" },
    ],
  },

  skill_rules: {
    title: "Skill Routing Rules",
    subtitle: "Map calls to required skills based on DID, IVR path, or campaign.",
    columns: [{ key: "data.match_kind", label: "Match" }],
    dataFields: [
      { key: "match_kind", label: "Match on", type: "select", options: ["did","ivr_path","campaign","caller_id"] },
      { key: "match_value", label: "Match value", type: "text" },
      { key: "skills", label: "Required skills", type: "multiref", refKind: "skills" },
    ],
  },

  vip_rules: {
    title: "VIP / Priority Rules",
    subtitle: "Priority bumps for callers matching customer tier or specific numbers.",
    columns: [{ key: "data.match_kind", label: "Match" }, { key: "data.priority_bump", label: "Bump" }],
    dataFields: [
      { key: "match_kind", label: "Match on", type: "select", options: ["caller_id","customer_tag","ltv_above","crm_tier"] },
      { key: "match_value", label: "Match value", type: "text" },
      { key: "priority_bump", label: "Priority bump (1-10)", type: "number" },
    ],
  },

  voicemail_boxes: {
    title: "Voicemail Boxes",
    subtitle: "Voicemail destinations with optional STT transcription.",
    columns: [{ key: "data.transcribe", label: "STT" }, { key: "data.notify_email", label: "Notify" }],
    dataFields: [
      { key: "greeting_audio", label: "Greeting audio", type: "file", uploadKind: "voicemail", accept: "audio/*" },
      { key: "transcribe", label: "Transcribe to text", type: "checkbox" },
      { key: "notify_email", label: "Notify email", type: "text" },
    ],
  },

  did_routes: {
    title: "DID Routing (time-of-day)",
    subtitle: "Time-of-day / weekday routing per DID.",
    columns: [],
    dataFields: [
      { key: "did_id", label: "DID", type: "ref", refKind: "dids" },
      { key: "day_target_kind", label: "Day route kind", type: "select", options: ["queue","ivr","voicemail","external"] },
      { key: "after_hours_target_kind", label: "After-hours route kind", type: "select", options: ["queue","ivr","voicemail","external","close_message"] },
      { key: "weekend_target_kind", label: "Weekend route kind", type: "select", options: ["queue","ivr","voicemail","external","close_message"] },
    ],
  },

  webchat_widgets: {
    title: "Webchat Widgets",
    subtitle: "Embeddable JS widgets per customer site.",
    columns: [{ key: "data.domain", label: "Domain" }],
    dataFields: [
      { key: "domain", label: "Allowed domain", type: "text" },
      { key: "greeting", label: "Greeting message", type: "text" },
      { key: "primary_color", label: "Primary color", type: "text" },
      { key: "pre_chat_form", label: "Pre-chat form", type: "checkbox" },
    ],
  },

  wa_templates: {
    title: "WhatsApp Templates",
    subtitle: "Pre-approved Meta templates for outbound after the 24-hour window.",
    columns: [{ key: "data.language", label: "Lang" }, { key: "data.category", label: "Category" }],
    dataFields: [
      { key: "template_id", label: "Template ID", type: "text" },
      { key: "language", label: "Language", type: "text" },
      { key: "category", label: "Category", type: "select", options: ["MARKETING","UTILITY","AUTHENTICATION"] },
      { key: "body", label: "Body (with {{1}}, {{2}})", type: "textarea" },
    ],
  },

  email_signatures: { title: "Email Signatures", subtitle: "Reusable signatures with team variables.", columns: [], dataFields: [{ key: "html", label: "HTML signature", type: "textarea" }] },
  canned_responses: { title: "Canned Responses", subtitle: "Quick replies usable across channels.", columns: [{ key: "data.shortcut", label: "Shortcut" }], dataFields: [{ key: "shortcut", label: "Shortcut", type: "text", placeholder: "/refund" }, { key: "body", label: "Body", type: "textarea" }, { key: "channels", label: "Channels", type: "csv", placeholder: "whatsapp,email" }] },

  // ----- Agent UI -----
  agent_layouts: { title: "Agent Screen Layouts", subtitle: "Saved agent UI layouts.", columns: [{ key: "data.panels_count", label: "Panels" }], dataFields: [{ key: "left_panel", label: "Left panel", type: "select", options: ["caller_info","script","crm","notes","none"] }, { key: "center_panel", label: "Center panel", type: "select", options: ["dialer","conversation","screenpop","none"] }, { key: "right_panel", label: "Right panel", type: "select", options: ["ai_copilot","kb_search","timeline","none"] }] },

  // ----- AI / Speech -----
  llm_configs: {
    title: "LLM Configuration",
    subtitle: "Per-tenant LLM provider, default model, temperature.",
    columns: [{ key: "data.provider", label: "Provider" }, { key: "data.model", label: "Model" }],
    dataFields: [
      { key: "provider",    label: "Provider", type: "select", options: ["openai","anthropic","google_ai","emergent_llm"] },
      { key: "model",       label: "Default model", type: "text", placeholder: "gpt-5.2 / claude-sonnet-4.5 / gemini-3-flash" },
      { key: "temperature", label: "Temperature", type: "number", placeholder: "0.5" },
      { key: "max_tokens",  label: "Max tokens",  type: "number" },
      { key: "system_prompt", label: "System prompt prefix", type: "textarea" },
    ],
  },

  stt_configs: {
    title: "STT Configuration",
    subtitle: "Live + batch speech-to-text.",
    columns: [{ key: "data.provider", label: "Provider" }, { key: "data.language", label: "Language" }],
    dataFields: [
      { key: "provider", label: "Provider", type: "select", options: ["deepgram","openai_whisper","google_stt","azure_stt"] },
      { key: "model", label: "Model", type: "text", placeholder: "nova-3" },
      { key: "language", label: "Language", type: "text", placeholder: "en-IN" },
      { key: "punctuation", label: "Punctuation", type: "checkbox" },
      { key: "diarization", label: "Speaker diarization", type: "checkbox" },
    ],
  },

  kb_documents: {
    title: "Knowledge Base Documents",
    subtitle: "Documents indexed for AI Copilot RAG.",
    columns: [{ key: "data.source_url", label: "URL" }],
    dataFields: [
      { key: "source_url", label: "Source URL or path", type: "text" },
      { key: "tags", label: "Tags", type: "csv" },
    ],
  },

  ai_prompts: {
    title: "AI Prompt Templates",
    subtitle: "Prompts for suggested replies, post-call summaries, auto-disposition.",
    columns: [{ key: "data.purpose", label: "Purpose" }],
    dataFields: [
      { key: "purpose", label: "Purpose", type: "select", options: ["suggested_reply","post_call_summary","auto_disposition","sentiment_label","objection_handler"] },
      { key: "channel", label: "Channel", type: "select", options: ["voice","whatsapp","sms","email","webchat","any"] },
      { key: "prompt", label: "Prompt", type: "textarea" },
    ],
  },

  pii_rules: {
    title: "PII Redaction Rules",
    subtitle: "Mask sensitive content before storage.",
    columns: [{ key: "data.regex", label: "Pattern" }],
    dataFields: [
      { key: "regex", label: "Regex pattern", type: "text" },
      { key: "replacement", label: "Replacement", type: "text", placeholder: "[REDACTED]" },
      { key: "scope", label: "Apply to", type: "select", options: ["transcripts","messages","all"] },
    ],
  },

  qm_samples: { title: "QM Sampling Rules", subtitle: "Random sampling per agent / period.", columns: [{ key: "data.sample_pct", label: "Sample %" }, { key: "data.period", label: "Period" }], dataFields: [{ key: "sample_pct", label: "Sample %", type: "number" }, { key: "period", label: "Period", type: "select", options: ["day","week","month"] }, { key: "min_calls", label: "Min calls", type: "number" }] },
  coaching_plans: { title: "Coaching Plans", subtitle: "Coaching tasks assigned to agents.", columns: [], dataFields: [{ key: "assigned_to", label: "Assignee (user id)", type: "text" }, { key: "due_date", label: "Due", type: "text" }, { key: "objectives", label: "Objectives", type: "textarea" }] },
  qm_calibrations: { title: "QM Calibrations", subtitle: "Multi-evaluator agreement sessions.", columns: [], dataFields: [{ key: "evaluators", label: "Evaluators (csv emails)", type: "csv" }] },

  report_templates: { title: "Custom Report Templates", subtitle: "Saved dimension/measure templates.", columns: [{ key: "data.time_grain", label: "Grain" }], dataFields: [{ key: "dimensions", label: "Dimensions (csv)", type: "csv" }, { key: "measures", label: "Measures (csv)", type: "csv" }, { key: "time_grain", label: "Time grain", type: "select", options: ["hour","day","week","month","quarter"] }] },
  report_schedules: { title: "Scheduled Reports", subtitle: "Email / S3 / webhook delivery on cron schedule.", columns: [{ key: "data.frequency", label: "Cadence" }, { key: "data.format", label: "Format" }], dataFields: [{ key: "template_id", label: "Template (resource id)", type: "ref", refKind: "report_templates" }, { key: "frequency", label: "Cadence", type: "select", options: ["daily","weekly","monthly"] }, { key: "format", label: "Format", type: "select", options: ["csv","pdf","xlsx","json"] }, { key: "recipients", label: "Recipients (csv emails)", type: "csv" }] },
  leaderboards: { title: "Leaderboards", subtitle: "Gamified rankings.", columns: [{ key: "data.metric", label: "Metric" }, { key: "data.period", label: "Period" }], dataFields: [{ key: "metric", label: "Metric", type: "select", options: ["calls_handled","sales_closed","csat","quality_score","talk_time"] }, { key: "period", label: "Period", type: "select", options: ["day","week","month","quarter"] }, { key: "show_top_n", label: "Show top N", type: "number" }] },
  kpi_dashboards: { title: "KPI Dashboards", subtitle: "Real-time KPI dashboards (TV-friendly).", columns: [], dataFields: [{ key: "layout", label: "Layout", type: "select", options: ["1x4","2x2","3x3","wallboard"] }] },

  webhooks: {
    title: "Webhooks",
    subtitle: "External webhook subscriptions — fire on platform events.",
    columns: [{ key: "data.url", label: "URL" }, { key: "data.events", label: "Events" }],
    dataFields: [
      { key: "url",    label: "Webhook URL",  type: "text", required: true },
      { key: "events", label: "Events (csv)", type: "csv", placeholder: "call.completed,conversation.created" },
      { key: "secret", label: "Signing secret", type: "text" },
      { key: "active", label: "Active",       type: "checkbox" },
    ],
  },

  api_keys: {
    title: "API Keys",
    subtitle: "Tenant-scoped API keys. Rotate regularly.",
    columns: [{ key: "data.masked_key", label: "Key" }, { key: "data.scopes", label: "Scopes" }],
    dataFields: [
      { key: "masked_key",     label: "Masked key (display)",   type: "text", placeholder: "ck_••••••••a3F7" },
      { key: "scopes",         label: "Scopes (csv)", type: "csv", placeholder: "read,write" },
      { key: "rate_limit_rpm", label: "Rate limit (rpm)", type: "number", placeholder: "600" },
      { key: "expires_at",     label: "Expires at",   type: "text", placeholder: "2027-01-01" },
    ],
  },

  crm_mappings: { title: "CRM Field Mappings", subtitle: "Connect7 fields ↔ CRM custom fields.", columns: [{ key: "data.crm", label: "CRM" }, { key: "data.object", label: "Object" }], dataFields: [{ key: "crm", label: "CRM", type: "select", options: ["salesforce","zoho","freshdesk","hubspot","leadsquared","zendesk","ms_dynamics","servicenow"] }, { key: "object", label: "CRM object", type: "text", placeholder: "Lead / Contact / Account" }] },

  ticket_queues: { title: "Ticket Queues", subtitle: "Light-weight ticket queues with SLAs.", columns: [{ key: "data.sla_hours_first_response", label: "FR hrs" }, { key: "data.sla_hours_resolve", label: "Resolve hrs" }], dataFields: [{ key: "sla_hours_first_response", label: "First-response SLA (hrs)", type: "number" }, { key: "sla_hours_resolve", label: "Resolution SLA (hrs)", type: "number" }, { key: "default_assignee", label: "Default assignee", type: "text" }] },
  ticket_categories: { title: "Ticket Categories", subtitle: "Ticket taxonomy.", columns: [], dataFields: [{ key: "color", label: "Color", type: "text" }, { key: "queue", label: "Default queue (resource id)", type: "ref", refKind: "ticket_queues" }] },
  sla_policies: { title: "SLA Policies", subtitle: "Reusable SLA definitions.", columns: [{ key: "data.first_response_minutes", label: "FR min" }, { key: "data.resolve_minutes", label: "Res min" }], dataFields: [{ key: "first_response_minutes", label: "First-response (min)", type: "number" }, { key: "resolve_minutes", label: "Resolve (min)", type: "number" }, { key: "business_hours_only", label: "Business hours only", type: "checkbox" }] },

  retention_policies: { title: "Data Retention Policies", subtitle: "GDPR / DPDP compliant retention.", columns: [{ key: "data.recordings_days", label: "Rec days" }], dataFields: [{ key: "recordings_days", label: "Recordings retention (days)", type: "number" }, { key: "transcripts_days", label: "Transcripts retention (days)", type: "number" }, { key: "messages_days", label: "Messages retention (days)", type: "number" }, { key: "legal_hold", label: "Legal hold", type: "checkbox" }] },
  ip_allowlists: { title: "IP Allowlist", subtitle: "Restrict admin access to specific CIDR ranges.", columns: [{ key: "data.cidr", label: "CIDR" }], dataFields: [{ key: "cidr", label: "CIDR", type: "text", placeholder: "203.0.113.0/24" }, { key: "applies_to", label: "Applies to roles (csv)", type: "csv", placeholder: "admin,super_admin" }] },
  consent_templates: { title: "Consent Templates", subtitle: "Recording disclosures and consent prompts.", columns: [{ key: "data.language", label: "Lang" }], dataFields: [{ key: "language", label: "Language", type: "text" }, { key: "audio_id", label: "Audio prompt", type: "ref", refKind: "audio_prompts" }, { key: "text", label: "Text version", type: "textarea" }] },

  rate_plans: { title: "Rate Plans", subtitle: "Per-tenant pricing — seats, minutes, messages.", columns: [{ key: "data.seat_inr", label: "Seat ₹" }, { key: "data.minute_inr", label: "Minute ₹" }], dataFields: [{ key: "seat_inr", label: "Seat (₹/month)", type: "number" }, { key: "minute_inr", label: "Voice minute (₹)", type: "number" }, { key: "wa_msg_inr", label: "WhatsApp msg (₹)", type: "number" }, { key: "sms_msg_inr", label: "SMS msg (₹)", type: "number" }] },

  // working_hours_profiles + compliance_rules + channel_configs handled by other custom pages
  working_hours_profiles: { title: "Working Hours Profiles", subtitle: "Reusable open-hour profiles. Reference these in Queues & Campaigns.", columns: [{ key: "data.start", label: "Start" }, { key: "data.end", label: "End" }, { key: "data.tz", label: "TZ" }], dataFields: [{ key: "start", label: "Open time", type: "time" }, { key: "end", label: "Close time", type: "time" }, { key: "tz", label: "Time zone", type: "select", options: ["Asia/Kolkata","Asia/Singapore","Asia/Dubai","Europe/London","America/New_York","America/Los_Angeles","Australia/Sydney"] }, { key: "days", label: "Days open (csv: mon,tue,wed)", type: "csv", placeholder: "mon,tue,wed,thu,fri" }] },

  compliance_rules: {
    title: "Compliance Rules",
    subtitle: "TCPA / TRAI / Ofcom — calling hours, drop rate, AMD limits.",
    columns: [{ key: "data.region", label: "Region" }, { key: "data.drop_rate_max_pct", label: "Drop %" }],
    dataFields: [
      { key: "region", label: "Region", type: "select", options: ["IN","US","UK","AE","SG","AU","EU"] },
      { key: "calling_start_local", label: "Calling start (local)", type: "time" },
      { key: "calling_end_local",   label: "Calling end (local)",   type: "time" },
      { key: "drop_rate_max_pct",      label: "Max drop %",     type: "number" },
      { key: "abandoned_max_pct",      label: "Max abandon %",  type: "number" },
      { key: "consent_required",       label: "Consent required", type: "checkbox" },
      { key: "company_id_play",        label: "Play company-ID prompt", type: "checkbox" },
    ],
  },

  channel_configs: {
    title: "Channel Configurations",
    subtitle: "Per-channel enable/disable + parameters. Most users go via the Channels Hub.",
    columns: [{ key: "data.channel", label: "Channel" }, { key: "data.enabled", label: "Enabled" }],
    dataFields: [
      { key: "channel", label: "Channel", type: "select", options: ["voice","whatsapp","sms","email","webchat","instagram","facebook","telegram"] },
      { key: "enabled", label: "Enabled", type: "checkbox" },
      { key: "engine",  label: "Engine / provider", type: "text" },
      { key: "session_window_hours", label: "Session window (hours)", type: "number" },
    ],
  },

  broadcast_messages: {
    title: "Voice Broadcast",
    subtitle: "Auto-play voice messages with no agent.",
    columns: [],
    dataFields: [
      { key: "audio_id", label: "Audio prompt", type: "ref", refKind: "audio_prompts" },
      { key: "max_attempts", label: "Max attempts", type: "number" },
    ],
  },

  scorecards: { _custom: true },
  campaigns:   { _custom: true },
  ivr_flows:   { _custom: true },
  dids:        { _custom: true },
  lead_lists:  { _custom: true },
  dnc_lists:   { _custom: true },
};

export const DEFAULT_SCHEMA = {
  title: "Configuration",
  subtitle: "Manage entries for this section.",
  columns: [],
  dataFields: [],
};

export function getSchema(kind) { return RESOURCE_SCHEMAS[kind] || DEFAULT_SCHEMA; }
export function pickPath(obj, path) { return path.split(".").reduce((o, k) => (o == null ? undefined : o[k]), obj); }
